import React, { useEffect, useMemo, useState } from 'react'
import { MessageCircle, X, Send, Trash2, Paperclip } from 'lucide-react'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import { ClientChatService } from '../../services/ClientChatService'

const STORAGE_KEY = 'omnia_web_chat_identity'

const generateSessionId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID()
    }
    const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
    return template.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

const FloatingChatWidget = ({ mode, isOpenDefault = false, embedded = false }) => {
    const { profile } = useAuth() || {}
    const [open, setOpen] = useState(isOpenDefault)
    const [step, setStep] = useState(mode === 'web' ? 'form' : 'chat')
    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [email, setEmail] = useState('')
    const [identityLoading, setIdentityLoading] = useState(false)
    const [identityError, setIdentityError] = useState(null)
    const [contactId, setContactId] = useState(null)
    const [sessionId, setSessionId] = useState(null)
    const [messages, setMessages] = useState([])
    const [messageInput, setMessageInput] = useState('')
    const [sending, setSending] = useState(false)
    const [loadingMessages, setLoadingMessages] = useState(false)
    const messagesEndRef = React.useRef(null)
    const fileInputRef = React.useRef(null)
    const [sendError, setSendError] = useState(null)
    const [attachment, setAttachment] = useState(null)
    const [uploading, setUploading] = useState(false)

    const isWeb = mode === 'web'

    const alignmentForMessage = useMemo(
        () => (msg) => {
            const mine = msg.direction === 'inbound'
            return {
                alignClass: mine ? 'justify-end' : 'justify-start',
                bubbleClass: mine
                    ? 'bg-blue-600 text-white rounded-l-2xl rounded-br-sm'
                    : 'bg-white text-gray-900 border border-gray-200 rounded-r-2xl rounded-bl-sm'
            }
        },
        []
    )

    useEffect(() => {
        if (!isWeb) return

        try {
            const raw = window.localStorage.getItem(STORAGE_KEY)
            if (!raw) return
            const parsed = JSON.parse(raw)
            const storedSessionId = parsed.sessionId
            if (!storedSessionId) return
            setSessionId(storedSessionId)
            setName(parsed.name || '')
            setPhone(parsed.phone || '')
            setEmail(parsed.email || '')
            setStep('chat')
        } catch {
            return
        }
    }, [isWeb])

    useEffect(() => {
        if (mode !== 'portal') return
        if (!profile?.id) return

        let active = true
        const resolveContact = async () => {
            setIdentityLoading(true)
            setIdentityError(null)
            try {
                const { data: contactData } = await supabase
                    .from('contacts')
                    .select('id')
                    .eq('portal_user_id', profile.id)
                    .maybeSingle()

                let resolvedId = contactData?.id || null

                if (!resolvedId) {
                    const { data: directContact } = await supabase
                        .from('contacts')
                        .select('id')
                        .eq('id', profile.id)
                        .maybeSingle()
                    if (directContact) {
                        resolvedId = profile.id
                    }
                }

                if (!active) return

                if (resolvedId) {
                    setContactId(resolvedId)
                    setStep('chat')
                } else {
                    setIdentityError('No pudimos vincular tu perfil a un contacto.')
                }
            } catch {
                if (!active) return
                setIdentityError('Ocurrió un error al cargar tu información.')
            } finally {
                if (active) {
                    setIdentityLoading(false)
                }
            }
        }

        resolveContact()

        return () => {
            active = false
        }
    }, [mode, profile])

    useEffect(() => {
        if (!contactId && !sessionId) return

        let cancelled = false
        let unsubscribe = null

        const loadMessages = async () => {
            setLoadingMessages(true)
            try {
                let query = supabase.from('messages').select('*').order('created_at', {
                    ascending: true
                })

                if (contactId) {
                    query = query.eq('contact_id', contactId)
                }

                if (!contactId && sessionId) {
                    query = query.eq('session_id', sessionId)
                }

                const { data, error } = await query
                if (error) throw error
                if (!cancelled) {
                    setMessages(data || [])
                }
            } catch {
                if (!cancelled) {
                    setMessages([])
                }
            } finally {
                if (!cancelled) {
                    setLoadingMessages(false)
                }
            }
        }

        loadMessages()

        unsubscribe = ClientChatService.subscribeToMyMessages({
            contact_id: contactId,
            session_id: sessionId,
            callback: (msg) => {
                if (msg.direction === 'inbound') return
                setMessages((prev) => [...prev, msg])
            }
        })

        return () => {
            cancelled = true
            if (unsubscribe) unsubscribe()
        }
    }, [contactId, sessionId])

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages, step, open])

    useEffect(() => {
        if (window.parent && window.parent !== window) {
            const message = open ? 'chat-opened' : 'chat-closed'
            window.parent.postMessage(message, '*')
        }
    }, [open])

    const handleToggle = () => {
        setOpen((prev) => !prev)
    }

    const handleEndSession = () => {
        if (window.confirm('¿Quieres finalizar esta conversación y empezar de nuevo?')) {
            window.localStorage.removeItem(STORAGE_KEY)
            setSessionId(null)
            setContactId(null)
            setMessages([])
            setName('')
            setPhone('')
            setEmail('')
            setStep('form')
            setIdentityError(null)
        }
    }

    const handleFormSubmit = async (e) => {
        e.preventDefault()
        const trimmedName = name.trim()
        const trimmedPhone = phone.trim()
        const trimmedEmail = email.trim()

        if (!trimmedName || (!trimmedPhone && !trimmedEmail)) {
            setIdentityError(
                'Por favor completa tu nombre y al menos un medio de contacto (celular o correo).'
            )
            return
        }

        setIdentityLoading(true)
        setIdentityError(null)

        try {
            const newSessionId = sessionId || generateSessionId()

            await ClientChatService.registerGuestVisitor({
                sessionId: newSessionId,
                name: trimmedName,
                phone: trimmedPhone,
                email: trimmedEmail
            })

            setContactId(null)
            setSessionId(newSessionId)
            setStep('chat')

            try {
                const payload = {
                    sessionId: newSessionId,
                    name: trimmedName,
                    phone: trimmedPhone,
                    email: trimmedEmail
                }
                window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
            } catch {
                return
            }
        } catch {
            setIdentityError('No pudimos registrar tus datos. Intenta de nuevo.')
        } finally {
            setIdentityLoading(false)
        }
    }

    const handleAttachmentClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const handleAttachmentChange = (e) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return
        const maxSize = 10 * 1024 * 1024
        if (file.size > maxSize) {
            setSendError('El archivo excede el tamaño máximo permitido (10MB).')
            setAttachment(null)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
            return
        }
        setAttachment(file)
        setSendError(null)
    }

    const handleSendMessage = async (e) => {
        e.preventDefault()
        if (!messageInput.trim() && !attachment) {
            setSendError('Escribe un mensaje o adjunta un archivo.')
            return
        }
        if (!contactId && !sessionId) {
            setSendError('No pudimos iniciar la conversación. Recarga la página e inténtalo de nuevo.')
            return
        }

        setSendError(null)
        setSending(true)
        try {
            let attachments = []
            if (attachment) {
                setUploading(true)
                const bucketName = 'documents'
                const cleanName = attachment.name.replace(/[^a-zA-Z0-9.-]/g, '_')
                const basePath = sessionId || contactId || 'general'
                const filePath = `chat/${basePath}/${Date.now()}_${cleanName}`
                const { error: uploadError } = await supabase.storage
                    .from(bucketName)
                    .upload(filePath, attachment)
                if (uploadError) {
                    setSendError('No se pudo subir el archivo. Intenta de nuevo.')
                    return
                }
                const {
                    data: { publicUrl }
                } = supabase.storage.from(bucketName).getPublicUrl(filePath)
                attachments = publicUrl ? [publicUrl] : []
            }
            const contentFallback = attachment && !messageInput.trim() ? attachment.name : messageInput
            const created = await ClientChatService.sendMessage({
                content: contentFallback,
                contact_id: contactId,
                session_id: sessionId,
                mode,
                attachments
            })
            setMessages((prev) => [...prev, created])
            setMessageInput('')
            setAttachment(null)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        } catch (error) {
            setSendError('No pudimos enviar tu mensaje. Intenta de nuevo.')
        } finally {
            setSending(false)
            setUploading(false)
        }
    }

    const renderHeader = () => (
        <div className="flex-none shrink-0 z-10 flex items-center justify-between px-4 py-3 border-b border-blue-700/20 bg-blue-700 text-white">
            <div>
                <p className="text-sm font-semibold">¿Necesitas ayuda?</p>
                <p className="text-xs text-blue-100">Chatea con el equipo de Gallegos Asesores</p>
            </div>
            <div className="flex items-center gap-2">
                {isWeb && step === 'chat' && (
                    <button
                        type="button"
                        onClick={handleEndSession}
                        className="p-1.5 rounded-full hover:bg-blue-600 text-blue-100 hover:text-white"
                        title="Finalizar y empezar de nuevo"
                    >
                        <Trash2 size={16} />
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleToggle}
                    className="p-1.5 rounded-full hover:bg-blue-600"
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    )

    const renderForm = () => (
        <form onSubmit={handleFormSubmit} className="flex flex-col gap-3 px-4 py-4">
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nombre</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                    placeholder="Cómo te llamas"
                />
            </div>
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                    placeholder="10 dígitos"
                />
            </div>
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Correo electrónico</label>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                    placeholder="tu@correo.com"
                />
            </div>
            {identityError && <p className="text-xs text-red-500 mt-1">{identityError}</p>}
            <button
                type="submit"
                disabled={identityLoading}
                className="mt-1 inline-flex items-center justify-center rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {identityLoading ? 'Guardando...' : 'Comenzar chat'}
            </button>
        </form>
    )

    const renderMessages = () => (
        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2 bg-gray-50">
            {loadingMessages ? (
                <div className="h-full flex items-center justify-center text-xs text-gray-400">
                    Cargando mensajes...
                </div>
            ) : messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-gray-400 text-center px-4">
                    Escribe tu mensaje y un asesor te responderá en breve.
                </div>
            ) : (
                messages.map((msg) => {
                    const { alignClass, bubbleClass } = alignmentForMessage(msg)
                    return (
                        <div key={msg.id} className={`flex ${alignClass}`}>
                            <div className="max-w-[80%]">
                                <div className={`px-3 py-2 text-sm shadow-sm ${bubbleClass}`}>
                                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                                    {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                                        <div className="mt-1 space-y-1">
                                            {msg.attachments.map((url, index) => (
                                                <a
                                                    key={index}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-[11px] underline"
                                                >
                                                    Archivo adjunto {index + 1}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-1 flex items-center justify-end gap-1 text-[10px] opacity-70">
                                        <span>
                                            {new Date(msg.created_at).toLocaleTimeString([], {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                })
            )}
            <div ref={messagesEndRef} />
        </div>
    )

    const renderChat = () => (
        <div className="flex-1 min-h-0 flex flex-col">
            {renderMessages()}
            <form
                onSubmit={handleSendMessage}
                className="flex-none shrink-0 z-10 border-t border-gray-200 bg-white px-3 py-2 flex items-center gap-2"
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleAttachmentChange}
                />
                <button
                    type="button"
                    onClick={handleAttachmentClick}
                    className="p-2 rounded-full text-gray-500 hover:text-gray-700"
                >
                    <Paperclip size={16} />
                </button>
                <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Escribe tu mensaje..."
                    className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 bg-gray-50"
                />
                <button
                    type="submit"
                    disabled={sending || uploading || (!messageInput.trim() && !attachment) || (!contactId && !sessionId)}
                    className="p-2 rounded-full bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <Send size={16} />
                </button>
            </form>
            {sendError && (
                <div className="px-3 pb-2 text-xs text-red-500">
                    {sendError}
                </div>
            )}
        </div>
    )

    const renderBody = () => {
        if (isWeb && step === 'form') {
            return renderForm()
        }

        if (mode === 'portal' && identityLoading && !contactId) {
            return (
                <div className="flex-1 flex items-center justify-center text-xs text-gray-400">
                    Cargando tu información...
                </div>
            )
        }

        if (mode === 'portal' && identityError && !contactId) {
            return (
                <div className="flex-1 flex flex-col items-center justify-center text-xs text-gray-500 px-4 text-center">
                    <p>{identityError}</p>
                </div>
            )
        }

        return renderChat()
    }

    const positionClasses = embedded ? 'bottom-0 right-0' : 'bottom-4 right-4 md:bottom-6 md:right-6'

    return (
        <div className={`fixed ${positionClasses} z-50 flex flex-col items-end justify-end bg-transparent pointer-events-none`}>
            <div className="pointer-events-auto">
                {open ? (
                    <div className="w-80 max-w-xs md:w-80 lg:w-96 h-[500px] max-h-[80vh] rounded-2xl shadow-2xl border border-blue-700/20 bg-white flex flex-col overflow-hidden">
                        {renderHeader()}
                        {renderBody()}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={handleToggle}
                        className="inline-flex items-center justify-center rounded-full bg-blue-700 text-white shadow-lg hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-700 w-12 h-12"
                    >
                        <MessageCircle size={22} />
                    </button>
                )}
            </div>
        </div>
    )
}

export default FloatingChatWidget
